import React from "react";
import { EmptyIllustration } from "./illustrations";

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Button({ className = "", variant = "primary", ...props }) {
  return <button className={cn("uiButton", `uiButton-${variant}`, className)} {...props} />;
}

export function Card({ className = "", ...props }) {
  return <div className={cn("uiCard", className)} {...props} />;
}

export function Badge({ className = "", tone = "neutral", ...props }) {
  return <span className={cn("uiBadge", `uiBadge-${tone}`, className)} {...props} />;
}

// Paleta de cores determinísticas — idêntica ao Claude Design v2.0
const AVATAR_COLORS = ['#25D366','#3B9DFF','#A78BFA','#F471B5','#F5A524','#34D2C8','#FF7849'];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  const seed = [...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[seed % AVATAR_COLORS.length];
}

export function Avatar({ name = "SZ", className = "", size = "md", src = null, status = null }) {
  // status: "online" | "away" | "offline" | null — renderiza um dot no canto
  const initials = String(name || "SZ")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "SZ";

  const [imgError, setImgError] = React.useState(false);
  const showImage = src && !imgError;

  const color = getAvatarColor(name);
  const colorStyle = showImage ? {} : { background: color + "22", color, borderColor: color + "44" };

  return (
    <div
      className={cn("uiAvatar", `uiAvatar-${size}`, showImage ? "uiAvatar-hasImg" : "", status ? "uiAvatar-hasStatus" : "", className)}
      style={colorStyle}
    >
      {showImage ? (
        <img
          src={src}
          alt={name}
          onError={() => setImgError(true)}
          loading="lazy"
        />
      ) : initials}
      {status && (
        <span
          className={`uiAvatarStatus uiAvatarStatus-${status}`}
          title={status === "online" ? "Online" : status === "away" ? "Ausente" : "Offline"}
          aria-label={status}
        />
      )}
    </div>
  );
}

export function StatusPill({ className = "", tone = "neutral", children, ...props }) {
  return (
    <span className={cn("uiStatusPill", `uiStatusPill-${tone}`, className)} {...props}>
      <i />
      <span>{children}</span>
    </span>
  );
}

export function PageHeader({ title, text, actions, className = "" }) {
  return (
    <header className={cn("uiPageHeader", className)}>
      <div className="uiPageHeader-copy">
        <h2>{title}</h2>
        {text && <p>{text}</p>}
      </div>
      {actions && <div className="uiPageHeader-actions">{actions}</div>}
    </header>
  );
}

export function Tabs({ items = [], value, onChange, className = "" }) {
  return (
    <div className={cn("uiTabs", className)}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          className={cn("uiTab", value === item.value && "active")}
          onClick={() => onChange?.(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export { FilterDrawer, FilterSection, FilterChipGroup, ActiveFilters, FilterDateRange, useEscClose } from "./filters";
export { SkeletonBlock, SkeletonAvatar, SkeletonText, SkeletonList, SkeletonTable } from "./skeletons";
export { CommandPalette, useCommandHotkey } from "./commandPalette";

export function EmptyState({ icon = "•", illustration, title, text, action }) {
  return (
    <div className={cn("uiEmptyState", illustration && "uiEmptyState-illustrated")}>
      {illustration ? (
        <EmptyIllustration name={illustration} />
      ) : (
        <div className="uiEmptyIcon">{icon}</div>
      )}
      {title && <h3>{title}</h3>}
      {text && <p>{text}</p>}
      {action && <div className="uiEmptyActions">{action}</div>}
    </div>
  );
}

export { EmptyIllustration };

export function Modal({ title, children, actions }) {
  return (
    <div className="modalOverlay">
      <div className="modalBox">
        {title && <h3>{title}</h3>}
        {children}
        {actions && <div className="modalActions">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * Floating bar that appears when items are selected in a list.
 * `count` ativa a barra; `actions` recebe array { label, onClick, variant?, icon?, disabled? }.
 * Variants: "primary" (default) | "ghost" | "danger".
 */
export function BulkActionBar({ count = 0, total, onClear, actions = [], children }) {
  const visible = count > 0;
  return (
    <div
      className={cn("uiBulkBar", visible && "uiBulkBar-show")}
      role="region"
      aria-label="Ações em lote"
      aria-hidden={!visible}
    >
      <div className="uiBulkBar-info">
        <span className="uiBulkBar-count">{count}</span>
        <span className="uiBulkBar-text">
          {count === 1 ? "item selecionado" : "itens selecionados"}
          {total ? <em> de {total}</em> : null}
        </span>
        {onClear && (
          <button type="button" className="uiBulkBar-clear" onClick={onClear}>
            Limpar
          </button>
        )}
      </div>
      <div className="uiBulkBar-actions">
        {children}
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            className={cn("uiBulkBar-btn", `uiBulkBar-btn-${a.variant || "primary"}`)}
            onClick={a.onClick}
            disabled={a.disabled}
            title={a.title}
          >
            {a.icon}
            <span>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Tri-state checkbox for "select all" headers.
 * checked=true (all), false (none), "indeterminate" (some).
 */
export function BulkCheckbox({ checked, onChange, label, className = "", ...props }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = checked === "indeterminate";
  }, [checked]);
  return (
    <label className={cn("uiBulkCheck", className)} onClick={(e) => e.stopPropagation()}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked === true}
        onChange={(e) => onChange?.(e.target.checked)}
        {...props}
      />
      <span className="uiBulkCheck-box" aria-hidden="true" />
      {label && <span className="uiBulkCheck-label">{label}</span>}
    </label>
  );
}
